import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarBlank, Plus, Trash } from "@phosphor-icons/react";

const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];

export const BusinessCalendarsManager = () => {
  const [calendars, setCalendars] = useState([]);
  const [newCal, setNewCal] = useState({ name: "", start_hour: 9, end_hour: 17, work_days: [0, 1, 2, 3, 4] });
  const load = () => api.get("/business-calendars").then(r => setCalendars(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const createCalendar = async () => {
    if (!newCal.name.trim()) return toast.error("Calendar name required");
    if (!newCal.work_days.length) return toast.error("Pick at least one work day");
    try {
      await api.post("/business-calendars", { ...newCal, name: newCal.name.trim() });
      toast.success("Calendar created");
      setNewCal({ name: "", start_hour: 9, end_hour: 17, work_days: [0, 1, 2, 3, 4] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed (admin only)"); }
  };
  const deleteCalendar = async (id) => { try { await api.delete(`/business-calendars/${id}`); toast.success("Calendar removed"); load(); } catch (e) { toast.error("Delete failed"); } };
  const toggleDay = (d) => setNewCal(c => ({ ...c, work_days: c.work_days.includes(d) ? c.work_days.filter(x => x !== d) : [...c.work_days, d].sort() }));

  return (
    <>
      <div className="flex items-center gap-2 mb-3 mt-10">
        <CalendarBlank size={16} className="text-primary" />
        <span className="text-[11px] font-mono uppercase tracking-widest text-primary">Reusable Business-Hour Calendars</span>
        <span className="text-[10px] font-mono text-muted-foreground">(tag to a client on the Routing Rules page)</span>
      </div>

      <Card className="p-5 border-border rounded-sm mb-4" data-testid="calendar-create-card">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-[10px] font-mono uppercase text-muted-foreground">Calendar name</Label>
            <Input data-testid="cal-name-input" className="mt-1 rounded-sm w-48" placeholder="e.g. US EST 8–6" value={newCal.name} onChange={(e) => setNewCal({ ...newCal, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase text-muted-foreground">Work days</Label>
            <div className="flex gap-1.5 mt-1">
              {DAYS.map(([label, d]) => (
                <button key={d} data-testid={`cal-day-${d}`} onClick={() => toggleDay(d)}
                  className={`h-9 w-11 rounded-sm border text-xs font-mono transition-colors ${newCal.work_days.includes(d) ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:border-primary/60'}`}>{label}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase text-muted-foreground">Start (IST)</Label>
            <Input type="number" min="0" max="23" data-testid="cal-start" className="mt-1 rounded-sm font-mono w-20" value={newCal.start_hour} onChange={(e) => setNewCal({ ...newCal, start_hour: parseInt(e.target.value || 0) })} />
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase text-muted-foreground">End (IST)</Label>
            <Input type="number" min="0" max="24" data-testid="cal-end" className="mt-1 rounded-sm font-mono w-20" value={newCal.end_hour} onChange={(e) => setNewCal({ ...newCal, end_hour: parseInt(e.target.value || 0) })} />
          </div>
          <Button data-testid="cal-create-btn" onClick={createCalendar} className="rounded-sm font-mono uppercase text-xs h-9"><Plus size={14} className="mr-2" />Create Calendar</Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-3">Tip: for an overnight shift, set an end hour earlier than the start (e.g. start 18, end 6 = 6 PM → 6 AM next day).</p>
      </Card>

      {calendars.length > 0 && (
        <Card className="border-border rounded-sm overflow-hidden mb-6" data-testid="calendars-list">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
              <th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Work days</th><th className="text-left px-4 py-3">Hours (IST)</th><th></th>
            </tr></thead>
            <tbody>
              {calendars.map(c => (
                <tr key={c.id} className="border-b border-border/60" data-testid={`cal-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.work_days.map(d => DAYS.find(x => x[1] === d)?.[0]).join(", ")}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.start_hour}:00 – {c.end_hour}:00</td>
                  <td className="px-4 py-3 text-right"><button data-testid={`cal-del-${c.id}`} onClick={() => deleteCalendar(c.id)} className="text-muted-foreground hover:text-red-700 dark:hover:text-red-400"><Trash size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
};
